<template>
  <ConfirmDialog />
  <div class="mt-2">
    <div class="flex">
      <div class="flex flex-wrap mb-4">
        <h3>
          {{ t('apiKeys.title') }}
        </h3>
      </div>
      <div
        class="flex flex-wrap mb-4"
        style="margin-left: auto;"
      >
        <SpmButton
          :label="t('apiKeys.create')"
          icon="far fa-plus-circle"
          class-style="p-button p-button-success"
          :loading="loading"
          @click="toggleCreatePanel"
        />
      </div>
    </div>
    <div class="grid">
      <div class="col-12">
        <!-- Identifier Card -->
        <div
          v-if="shopIdentifier && !loading"
          class="info-card mb-5"
        >
          <div class="info-card__header">
            <h2 class="info-card__title">
              {{ t('apiKeys.identifier.title') }}
            </h2>
            <p class="info-card__description">
              {{ t('apiKeys.identifier.description') }}
            </p>
          </div>
          <div class="info-card__body">
            <div class="p-inputgroup">
              <InputText
                :value="shopIdentifier"
                readonly
              />
              <SpmButton
                icon="far fa-copy"
                class-style="p-button-secondary"
                :title="t('common.copy')"
                @click="copyIdentifier"
              />
            </div>
          </div>
        </div>

        <!-- API Keys Section Card -->
        <div class="info-card mb-5">
          <div class="info-card__header">
            <div class="flex justify-content-between align-items-start">
              <div>
                <h2 class="info-card__title">
                  {{ t('apiKeys.keysTitle') }}
                </h2>
                <p class="info-card__description">
                  {{ t('apiKeys.subtitle') }}
                </p>
              </div>
              <SpmButton
                icon="fas fa-sync-alt"
                class-style="p-button-text p-button-rounded"
                :loading="loading"
                :title="t('common.refresh')"
                @click="loadApiKeys"
              />
            </div>
          </div>
          <div class="info-card__body">
            <!-- API Keys List Container -->
            <div class="api-key-list-container">
              <!-- Loading State -->
              <div
                v-if="loading && apiKeys.length === 0"
                class="p-4 text-center"
              >
                <ProgressSpinner />
              </div>

              <!-- Empty State -->
              <div
                v-else-if="!loading && apiKeys.length === 0"
                class="empty-state"
              >
                <i class="fas fa-key empty-state__icon" />
                <h3 class="empty-state__title">
                  {{ t('apiKeys.empty.title') }}
                </h3>
                <p class="empty-state__description">
                  {{ t('apiKeys.empty.description') }}
                </p>
                <SpmButton
                  :label="t('apiKeys.createFirst')"
                  icon="fas fa-plus"
                  class-style="p-button-success"
                  @click="toggleCreatePanel"
                />
              </div>

              <!-- API Keys List -->
              <div
                v-else
                class="api-key-list"
              >
                <div
                  v-for="key in apiKeys"
                  :key="key.id"
                  class="api-key-item"
                >
                  <div class="api-key-item__header">
                    <div class="api-key-item__name-group">
                      <i class="fas fa-key api-key-item__icon" />
                      <span class="api-key-item__name">{{ key.key_name }}</span>
                    </div>
                    <SpmButton
                      icon="fas fa-trash-alt"
                      class-style="p-button-text p-button-danger p-button-rounded"
                      :title="t('apiKeys.revoke')"
                      @click="confirmRevoke(key)"
                    />
                  </div>
                  <div class="api-key-item__body">
                    <div class="api-key-item__prefix">
                      {{ key.prefix }}********************
                    </div>
                    <div class="api-key-item__details">
                      <div class="detail-group">
                        <span class="detail-group__label">{{ t('apiKeys.scopes') }}</span>
                        <div class="detail-group__value">
                          <Tag
                            :value="key.scopes.includes('all') ? t('apiKeys.allRights') : key.scopes.join(', ')"
                            :severity="key.scopes.includes('all') ? 'success' : 'info'"
                          />
                        </div>
                      </div>
                      <div class="detail-group">
                        <span class="detail-group__label">{{ t('apiKeys.rateLimit') }}</span>
                        <div class="detail-group__value">
                          <Tag
                            :value="key.rate_limit_per_min ? `${key.rate_limit_per_min}/min` : t('apiKeys.unlimited')"
                            :severity="key.rate_limit_per_min ? 'info' : 'success'"
                          />
                        </div>
                      </div>
                      <div class="detail-group">
                        <span class="detail-group__label">{{ t('apiKeys.created') }}</span>
                        <div class="detail-group__value">
                          {{ new Date(key.created_at).toLocaleDateString('fr-FR') }}
                        </div>
                      </div>
                      <div class="detail-group">
                        <span class="detail-group__label">{{ t('apiKeys.lastUsed') }}</span>
                        <div class="detail-group__value">
                          {{ key.last_used_at ? new Date(key.last_used_at).toLocaleString('fr-FR') : t('apiKeys.neverUsed') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Creation Overlay Panel -->
        <SpmOverlayPanel
          ref="createPanel"
          class-header="flex justify-content-between flex-wrap p-3 header-content"
          class-content="p-3"
          min-panel-width="400px"
          :close-panel="closeCreatePanel"
          @onclose-panel="closeCreatePanel = false"
        >
          <template #header>
            <div class="flex align-items-center">
              <i class="fas fa-key mr-2" />
              <span class="font-bold">{{ t('apiKeys.createModal.title') }}</span>
            </div>
          </template>

          <div class="field">
            <label
              for="keyName"
              class="block mb-2 font-medium"
            >{{ t('apiKeys.createModal.label') }}</label>
            <InputText
              id="keyName"
              v-model="createForm.key_name"
              :placeholder="t('apiKeys.createModal.labelPlaceholder')"
              class="w-full"
              :class="{ 'p-invalid': v$.key_name.$error }"
              autofocus
            />
            <small
              v-if="v$.key_name.$error"
              class="p-error block mt-1"
            >
              {{ v$.key_name.$errors[0].$message }}
            </small>
          </div>

          <template #footer>
            <div class="flex justify-content-end gap-2 p-2">
              <SpmButton
                :label="t('apiKeys.createModal.actions.generate')"
                icon="fas fa-plus"
                class-style="p-button-sm"
                :loading="loading"
                :disabled="v$.$invalid"
                @click="handleCreateKey"
              />
            </div>
          </template>
        </SpmOverlayPanel>

        <!-- Success Modal -->
        <Dialog
          v-model:visible="showSuccessModal"
          :header="t('apiKeys.successModal.title')"
          :modal="true"
          :style="{ width: '550px' }"
          :closable="false"
          class="api-key-success-modal"
        >
          <!-- Header avec icône et sous-titre -->
          <div class="text-center mb-4">
            <div class="success-icon mb-3">
              <i class="fas fa-shield-alt" />
            </div>
            <p class="text-color-secondary mb-0">
              {{ t('apiKeys.successModal.subtitle') }}
            </p>
          </div>

          <!-- Clé API -->
          <div class="mb-4">
            <label class="api-key-label">{{ t('apiKeys.successModal.keyLabel') }}</label>
            <div class="api-key-input">
              <InputText
                :value="createdKey?.full_key"
                readonly
                class="key-field"
              />
              <SpmButton
                :icon="keyCopied ? 'fas fa-check' : 'far fa-copy'"
                :label="keyCopied ? t('apiKeys.successModal.copied') : t('apiKeys.successModal.copy')"
                :class-style="keyCopied ? 'p-button-success' : 'p-button-primary'"
                @click="copyKey"
              />
            </div>
            <div
              v-if="!keyCopied"
              class="copy-reminder"
            >
              <i class="fas fa-info-circle mr-1" />
              {{ t('apiKeys.successModal.copyReminder') }}
            </div>
          </div>

          <!-- Informations -->
          <div class="key-info mb-4">
            <div class="info-row">
              <span class="info-label">{{ t('apiKeys.keyName') }}:</span>
              <span class="info-value">{{ createdKey?.key_name }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('apiKeys.identifier.title') }}:</span>
              <span class="info-value font-mono">{{ shopIdentifier }}</span>
            </div>
          </div>

          <template #footer>
            <div class="modal-footer">
              <SpmButton
                :label="keyCopied ? t('apiKeys.successModal.understand') : t('apiKeys.successModal.copyFirst')"
                :icon="keyCopied ? 'fas fa-check' : 'fas fa-copy'"
                :class-style="keyCopied ? 'p-button-success' : 'p-button-warning'"
                :disabled="!keyCopied"
                autofocus
                @click="showSuccessModal = false"
              />
            </div>
          </template>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import {
  defineComponent, onMounted, ref, computed,
} from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import ConfirmDialog from 'primevue/confirmdialog';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import {
  required, minLength, maxLength, helpers,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';

interface ApiKey {
  id: number;
  key_name: string;
  prefix: string;
  scopes: string[];
  rate_limit_per_min: number | null;
  created_at: string;
  last_used_at: string | null;
}

interface CreateApiKeyRequest {
  key_name: string;
}

interface CreateApiKeyResponse {
  id: number;
  full_key: string;
  key_name: string;
  prefix: string;
  scopes: string[];
  rate_limit_per_min: number | null;
  created_at: string;
}

export default defineComponent({
  name: 'ShopApiAccess',
  components: {
    SpmButton,
    Dialog,
    InputText,
    Tag,
    ProgressSpinner,
    ConfirmDialog,
    SpmOverlayPanel,
  },

  setup() {
    const { t } = useI18n();
    const confirm = useConfirm();

    // State
    const createPanel = ref();
    const closeCreatePanel = ref(false);
    const apiKeys = ref<ApiKey[]>([]);
    const shopIdentifier = ref<string | null>(null);
    const loading = ref(false);
    const showSuccessModal = ref(false);
    const createdKey = ref<CreateApiKeyResponse | null>(null);
    const keyCopied = ref(false);

    // Form
    const createForm = ref<CreateApiKeyRequest>({
      key_name: '',
    });

    const shopId = computed(() => UserState.activeShop?.id);

    // API Methods
    const loadApiKeys = async () => {
      if (!shopId.value) return;
      loading.value = true;
      try {
        const response = await nestCall('get', NestApiType.DEFAULT, `/shop/keys/${shopId.value}`);
        if (response?.success) {
          apiKeys.value = response.data.keys;
          shopIdentifier.value = response.data.identifiant;
        } else {
          throw new Error(response?.error?.message || t('apiKeys.errors.loadFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const revokeKey = async (key: ApiKey) => {
      if (!shopId.value) return;
      loading.value = true;
      try {
        const response = await nestCall('delete', NestApiType.DEFAULT, `/shop/keys/${shopId.value}/${key.id}`);
        if (response?.success) {
          apiKeys.value = apiKeys.value.filter((k) => k.id !== key.id);
          await showToastSuccess(t('apiKeys.toasts.revoked'));
        } else {
          throw new Error(response?.error?.message || t('apiKeys.errors.revokeFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const copyIdentifier = async () => {
      if (!shopIdentifier.value) return;
      try {
        await navigator.clipboard.writeText(shopIdentifier.value);
        await showToastSuccess(t('apiKeys.toasts.copied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyKey = async () => {
      if (!createdKey.value) return;
      try {
        await navigator.clipboard.writeText(createdKey.value.full_key);
        keyCopied.value = true;
        await showToastSuccess(t('apiKeys.toasts.copied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    // Custom validator for alphanumeric and spaces
    const alphaNumSpaces = helpers.withMessage(
      () => t('apiKeys.validation.alphaNumSpaces'),
      (value: string) => !value || /^[a-zA-Z0-9\s]+$/.test(value),
    );

    const rules = {
      key_name: {
        required: helpers.withMessage(() => t('vuelidate.required'), required),
        minLength: helpers.withMessage(() => t('vuelidate.minLength', { min: 3 }), minLength(3)),
        maxLength: helpers.withMessage(() => t('vuelidate.maxLength', { max: 20 }), maxLength(20)),
        alphaNumSpaces,
      },
    };

    const v$ = useVuelidate(rules, createForm);

    const toggleCreatePanel = (event: Event) => {
      createPanel.value.toggle(event);
      closeCreatePanel.value = false;
      // Trigger validation to show errors immediately
      v$.value.$touch();
    };

    const resetCreateForm = () => {
      createForm.value = { key_name: '' };
    };

    const handleCreateKey = async () => {
      if (!shopId.value) return;

      const success = await v$.value.$validate();

      if (!success) {
        return;
      }

      loading.value = true;
      try {
        const payload: CreateApiKeyRequest = {
          key_name: createForm.value.key_name,
        };
        const response = await nestCall('post', NestApiType.DEFAULT, `/shop/keys/${shopId.value}`, {}, '', payload);
        if (response?.success) {
          closeCreatePanel.value = true;
          createdKey.value = response.data;
          showSuccessModal.value = true;
          keyCopied.value = false;
          resetCreateForm();
          await loadApiKeys();
          await showToastSuccess(t('apiKeys.toasts.created'));
        } else {
          throw new Error(response?.error || t('apiKeys.errors.createFailed'));
        }
      } catch (err: any) {
        const errorMessage = err.message === 'api_keys_limit_reached'
          ? t('apiKeys.errors.limitReached')
          : t('apiKeys.errors.createFailed');
        await showToastError(errorMessage);
      } finally {
        loading.value = false;
      }
    };

    const confirmRevoke = (key: ApiKey) => {
      confirm.require({
        message: t('apiKeys.confirmRevoke', { prefix: key.prefix, name: key.key_name }),
        header: t('common.confirmation'),
        icon: 'fas fa-exclamation-triangle',
        accept: () => {
          revokeKey(key);
        },
      });
    };

    onMounted(loadApiKeys);

    return {
      t,
      apiKeys,
      shopIdentifier,
      loading,
      showSuccessModal,
      createdKey,
      keyCopied,
      createForm,
      createPanel,
      closeCreatePanel,
      loadApiKeys,
      handleCreateKey,
      confirmRevoke,
      copyKey,
      copyIdentifier,
      toggleCreatePanel,
      v$,
    };
  },
});
</script>

<style scoped lang="scss">
.api-key-success-modal {
  .success-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
    margin: 0 auto;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }

  .api-key-label {
    display: block;
    font-weight: 600;
    color: var(--text-color);
    margin-bottom: 0.75rem;
    font-size: 1rem;
  }

  .api-key-input {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;

    .key-field {
      flex: 1;
      font-family: 'Courier New', monospace;
      font-size: 0.85rem;
      background: var(--surface-100);
      border: 2px solid var(--surface-300);
      padding: 0.75rem;
      border-radius: 6px;
    }
  }

  .copy-reminder {
    background: #fff3cd;
    color: #856404;
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    border-left: 4px solid #ffc107;
    font-size: 0.85rem;
    font-weight: 500;
  }

  .key-info {
    background: var(--surface-50);
    border: 1px solid var(--surface-200);
    border-radius: 6px;
    padding: 1rem;

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0;

      &:not(:last-child) {
        border-bottom: 1px solid var(--surface-200);
      }

      .info-label {
        font-weight: 600;
        color: var(--text-color-secondary);
        font-size: 0.9rem;
      }

      .info-value {
        font-weight: 500;
        color: var(--text-color);
        font-size: 0.9rem;
      }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: center;
    padding-top: 0.5rem;
  }
}

@media (max-width: 768px) {
  .api-key-success-modal {
    ::v-deep(.p-dialog) {
      width: 95vw !important;
      margin: 1rem;
    }

    .api-key-input {
      flex-direction: column;
    }
  }
}

.info-card {
  background-color: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);

  &__header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--surface-border);
  }

  &__title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
  }

  &__body {
    padding: 1.25rem;
  }

  &__description {
    color: var(--text-color-secondary);
    margin: 0 0 0rem;
    font-size: 0.9rem;
    max-width: 700px;
  }
}

.api-key-list-container {
  background-color: var(--surface-card);
}

.api-key-list {
  display: flex;
  flex-direction: column;
}

.api-key-item {
  padding: 1.25rem;
  border-bottom: 1px solid var(--surface-border);
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--surface-hover);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
  }

  &__name-group {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__icon {
    color: var(--primary-color);
  }

  &__name {
    font-size: 1.1rem;
    font-weight: 600;
  }

  &__prefix {
    background-color: var(--surface-50);
    border: 1px solid var(--surface-200);
    padding: 0.75rem 1rem;
    border-radius: var(--border-radius);
    color: var(--text-color-secondary);
    word-break: break-all;
    font-size: 0.9rem;
    font-family: var(--font-family-monospace);
    margin-bottom: 1.25rem;
  }

  &__details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.25rem;
  }
}

.detail-group {
  &__label {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    margin-bottom: 0.25rem;
    text-transform: uppercase;
  }

  &__value {
    font-weight: 500;
  }
}

.empty-state {
  padding: 3rem;
  text-align: center;

  &__icon {
    font-size: 3rem;
    color: var(--text-color-secondary);
    margin-bottom: 1.5rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  &__description {
    color: var(--text-color-secondary);
    margin-bottom: 1.5rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
